/**
 * Environment Context Provider
 *
 * Manages environment state and switching across the application
 */

import type { EnvironmentResponse } from '@monorepo/shared/schemas/environments'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTenant } from '@/features/tenants'
import { toast } from '@/lib/toast'
import { environmentService } from '../services/environmentService'

interface EnvironmentContextValue {
  // Current active environment
  currentEnvironment: EnvironmentResponse | null
  // All environments for the tenant
  environments: EnvironmentResponse[]
  // Loading states
  isLoadingEnvironments: boolean
  isLoadingCurrentEnvironment: boolean
  isSwitchingEnvironment: boolean
  // Actions
  switchEnvironment: (environmentId: string) => Promise<void>
  setDefaultEnvironment: (environmentId: string) => Promise<void>
  refreshEnvironments: () => void
  // Error state
  environmentError: Error | null
}

const EnvironmentContext = createContext<EnvironmentContextValue | undefined>(
  undefined
)

interface EnvironmentProviderProps {
  children: ReactNode
}

export function EnvironmentProvider({ children }: EnvironmentProviderProps) {
  const queryClient = useQueryClient()
  const { currentTenant } = useTenant()
  const [currentEnvironmentId, setCurrentEnvironmentId] = useState<
    string | null
  >(() => environmentService.getCurrentEnvironmentId())
  const hasAutoSelected = useRef(false)

  // Fetch all environments for the current tenant
  const {
    data: environmentsResponse,
    isLoading: isLoadingEnvironments,
    error: environmentsError,
    refetch: refreshEnvironments,
  } = useQuery({
    queryKey: ['environments', currentTenant?.id],
    queryFn: () =>
      environmentService.getEnvironments({
        tenant_id: currentTenant?.id,
      }),
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const environments = environmentsResponse?.data || []

  // Fetch current environment details (only after tenant is loaded to avoid 401s)
  const { data: currentEnvironment, isLoading: isLoadingCurrentEnvironment } =
    useQuery({
      queryKey: ['environment', currentEnvironmentId],
      queryFn: () => environmentService.getEnvironment(currentEnvironmentId!),
      enabled: !!currentEnvironmentId && !!currentTenant?.id,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
    })

  // Switch environment mutation
  const switchEnvironmentMutation = useMutation({
    mutationFn: async (environmentId: string) => {
      // Validate environment belongs to current tenant
      const environment =
        await environmentService.switchEnvironment(environmentId)
      if (environment.tenant_id !== currentTenant?.id) {
        throw new Error('Environment does not belong to current tenant')
      }
      return environment
    },
    onSuccess: (environment, environmentId) => {
      setCurrentEnvironmentId(environmentId)

      // Invalidate ALL queries except auth, user, tenants, and environments
      // All tables have tenant + environment isolation, so we need to clear everything
      const queriesToInvalidate = [
        'agents',
        'signals',
        'features',
        'plans',
        'customers',
        'contracts',
        'invoices',
        'usage',
        'metrics',
        'events',
        'credits',
        'subscriptions',
        'api-keys',
      ]

      queriesToInvalidate.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })

      // Refetch current environment details
      queryClient.invalidateQueries({
        queryKey: ['environment', environmentId],
      })

      toast.success(`Switched to ${environment.name}`)
    },
    onError: () => {
      toast.error('Failed to switch environment')
    },
  })

  // Set user's default environment mutation (user-specific preference)
  const setDefaultEnvironmentMutation = useMutation({
    mutationFn: (environmentId: string) =>
      environmentService.setUserDefaultEnvironment(environmentId),
    onSuccess: () => {
      // Invalidate tenant query to refresh default_environment_id
      queryClient.invalidateQueries({ queryKey: ['tenant', currentTenant?.id] })
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast.success('Default environment updated')
    },
    onError: () => {
      toast.error('Failed to set default environment')
    },
  })

  // Validate stored environment ID and auto-select if needed
  // Combines validation (clear stale IDs) and auto-select (three-tier fallback) in one effect
  useEffect(() => {
    if (environments.length === 0 || hasAutoSelected.current) {
      return
    }

    // If we have a stored environment ID, validate it's still in the list
    if (currentEnvironmentId) {
      const isValid = environments.some(e => e.id === currentEnvironmentId)
      if (isValid) {
        hasAutoSelected.current = true
        return
      }
      // Stored ID is stale — clear it and fall through to auto-select
      localStorage.removeItem('current_environment_id')
    }

    // Auto-select with three-tier fallback:
    // 1. User's default_environment_id for this tenant (user preference)
    // 2. Tenant-wide default (is_default = true)
    // 3. First environment alphabetically
    let selectedEnvironment: EnvironmentResponse | undefined

    if (currentTenant?.default_environment_id) {
      selectedEnvironment = environments.find(
        e => e.id === currentTenant.default_environment_id
      )
    }

    if (!selectedEnvironment) {
      selectedEnvironment = environments.find(e => e.is_default)
    }

    if (!selectedEnvironment) {
      selectedEnvironment = environments[0]
    }

    if (selectedEnvironment) {
      hasAutoSelected.current = true
      setCurrentEnvironmentId(selectedEnvironment.id)
      localStorage.setItem('current_environment_id', selectedEnvironment.id)
    }
  }, [
    currentEnvironmentId,
    environments,
    currentTenant?.default_environment_id,
  ])

  // Clear environment context when tenant is removed (logout/switch),
  // but NOT on initial mount when tenant is still loading.
  const hadTenant = useRef(false)
  useEffect(() => {
    if (currentTenant) {
      hadTenant.current = true
    } else if (hadTenant.current) {
      // Tenant was previously set and is now cleared — clean up
      hadTenant.current = false
      setCurrentEnvironmentId(null)
      environmentService.clearEnvironmentContext()
      hasAutoSelected.current = false
    }
  }, [currentTenant])

  // Switch environment handler
  const handleSwitchEnvironment = async (environmentId: string) => {
    await switchEnvironmentMutation.mutateAsync(environmentId)
  }

  // Set default environment handler
  const handleSetDefaultEnvironment = async (environmentId: string) => {
    await setDefaultEnvironmentMutation.mutateAsync(environmentId)
  }

  const value: EnvironmentContextValue = {
    currentEnvironment: currentEnvironment || null,
    environments,
    isLoadingEnvironments,
    isLoadingCurrentEnvironment,
    isSwitchingEnvironment: switchEnvironmentMutation.isPending,
    switchEnvironment: handleSwitchEnvironment,
    setDefaultEnvironment: handleSetDefaultEnvironment,
    refreshEnvironments,
    environmentError: environmentsError || null,
  }

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  )
}

/**
 * Hook to use environment context
 */
export function useEnvironment() {
  const context = useContext(EnvironmentContext)

  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider')
  }

  return context
}
